import { MAX_GOAL_SELECTIONS, ONBOARDING_GOALS } from "@/config/onboarding/goals";

export const ONBOARDING_GOAL_SELECTION_STORAGE_KEY = "b3os_onboarding_goal_selection:v1";

export interface StoredOnboardingGoalSelection {
  goalIds: string[];
  goalTitles: string[];
  updatedAt: number;
}

const goalById = new Map(ONBOARDING_GOALS.map(goal => [goal.id, goal]));

export function normalizeOnboardingGoalIds(goalIds: string[]): string[] {
  return Array.from(new Set(goalIds.filter(goalId => goalById.has(goalId)))).slice(0, MAX_GOAL_SELECTIONS);
}

export function getOnboardingGoalTitles(goalIds: string[]): string[] {
  return normalizeOnboardingGoalIds(goalIds).map(goalId => goalById.get(goalId)?.title ?? goalId);
}

export function getRecommendedTemplateSlugsForGoalIds(goalIds: string[]): string[] {
  return normalizeOnboardingGoalIds(goalIds)
    .map(goalId => goalById.get(goalId)?.recommendedTemplateSlug)
    .filter((slug): slug is string => Boolean(slug));
}

export function persistOnboardingGoalSelection(goalIds: string[]): void {
  if (typeof window === "undefined") return;

  const normalizedGoalIds = normalizeOnboardingGoalIds(goalIds);
  const selection: StoredOnboardingGoalSelection = {
    goalIds: normalizedGoalIds,
    goalTitles: getOnboardingGoalTitles(normalizedGoalIds),
    updatedAt: Date.now(),
  };

  try {
    window.localStorage.setItem(ONBOARDING_GOAL_SELECTION_STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Best-effort personalization; onboarding should not depend on localStorage.
  }
}

export function readOnboardingGoalSelection(): StoredOnboardingGoalSelection | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(ONBOARDING_GOAL_SELECTION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredOnboardingGoalSelection>;
    if (!Array.isArray(parsed.goalIds) || typeof parsed.updatedAt !== "number") return null;

    const goalIds = normalizeOnboardingGoalIds(
      parsed.goalIds.filter((goalId): goalId is string => typeof goalId === "string"),
    );
    if (goalIds.length === 0) return null;

    return {
      goalIds,
      goalTitles: getOnboardingGoalTitles(goalIds),
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}
